<script setup lang="ts">
import { computed, ref } from 'vue'
import type { IInputFieldEvents, IInputFieldProps } from '../types'
import { PInput } from '../parts'
import useInputAutocompleteLogic from '@/ui/plushComponents/VInputField/composables/useInputAutocompleteLogic'
import { isDark } from '@/lib/utils'
import PSelectDropdown from '@/ui/plushComponents/parts/form/Dropdown/PSelectDropdown.vue'

const props = defineProps<IInputFieldProps<'autocomplete-search'>>()
const emit = defineEmits<IInputFieldEvents>()

const containerRef = ref<HTMLDivElement | null>(null)

const {
    isError,
    isOpen,
    flatOptions,
    highlightedIndex,
    readOnly,
    disabled,
    loading,
    selectOption,
    debouncedUpdateModelValue,
    handleFocus,
    handleClear,
    handleBlur,
    handleKeydown,
} = useInputAutocompleteLogic(props, emit, containerRef)

const options = computed(() => props.options)

const trailingIcon = computed<string | false>(() =>
    props.modelValue.length >= 1 && !disabled.value && !readOnly.value ? 'close' : false,
)
</script>

<template>
    <div
        ref="containerRef"
        class="relative max-w-[480px]"
        :class="[isDark(options.dark)]"
    >
        <PInput
            v-bind="options"
            :model-value="props.modelValue"
            :aria="{
                label: options.aria?.label || options.name || 'Input',
            }"
            :placeholder="options.placeholder || 'Wyszukaj'"
            leading-icon="search"
            :trailing-icon="trailingIcon"
            :auto-complete="options.autoComplete ?? 'off'"
            :auto-focus="options.autoFocus ?? true"
            :is-error="isError"
            :data-test="props.dataTest"
            :dark="false"
            @update:model-value="debouncedUpdateModelValue"
            @focus="handleFocus"
            @clear="handleClear"
            @blur="handleBlur"
            @keydown="handleKeydown"
        />
        <PSelectDropdown
            v-model:is-open="isOpen"
            :value="props.modelValue"
            :label-id="`${options.id}-label`"
            :dropdown-id="`${options.id}-dropdown`"
            :flat-options="flatOptions"
            :select-option="selectOption"
            :highlighted-index="highlightedIndex"
            :loading="loading"
            :disabled="disabled"
            :read-only="readOnly"
            :max-visible-items="options.maxVisibleItems"
            :focus-visible="false"
        />
        <HelperMessage
            v-if="options.helper"
            class="mt-2xs"
            size="SMALL"
            :message="options.helper"
        />
    </div>
</template>
